import type { ThemeId } from '@/shared/appearance/appearance'

import terysFaviconUrl from '@/assets/brandEmblem.png?url'

import smartomicaFaviconUrl from './smartomicaMark.png?url'

/**
 * WHOSE PRODUCT THIS IS, as the interface says it.
 *
 * A brand is a mark, a wordmark, a name and a face in the browser's tab — the
 * things a stylesheet cannot swap. The palette a white-label wears is a THEME
 * (`styles/themes/`), and the theme is where nearly all of a white-label
 * lives; this module exists for the remainder, which is artwork and identity.
 * `smartomica` is not a blue Terysling: a clinician signing in to it must meet
 * the vendor's own logotype where the host's would stand, and no amount of
 * token redefinition puts a different drawing on the screen.
 *
 * THE BRAND FOLLOWS THE THEME, and deliberately has no control of its own.
 * Two dials that can disagree would let an account run Smartomica's colours
 * under Terysling's mark, or the reverse, and neither is a product anybody
 * asked for. One choice, made in the appearance picker, decides both.
 *
 * DELIBERATELY DATA AND NOTHING ELSE — no hook, no component. Both directions
 * read it: `BrandLockup` draws what a frame signs with, and
 * `AppearanceProvider` stamps the document's face from it while applying the
 * appearance. A hook here would make the provider import a module that imports
 * the provider.
 */
export const BRANDS = ['terysling', 'smartomica'] as const
export type BrandId = (typeof BRANDS)[number]

/**
 * Every theme's brand, stated exhaustively.
 *
 * A `Record<ThemeId, …>` rather than a lookup with a fallback, because the
 * fallback is the bug: a new theme added to `THEMES` would silently wear the
 * host's mark, which is right for a Terysling theme and wrong for the next
 * white-label — and nothing would say which one had been assumed. Written out,
 * a new theme does not compile until somebody answers the question.
 *
 * TWO THEMES ANSWER `smartomica`, which is the map earning its keep rather
 * than a duplicate: the vendor has a shipped product and a print brand guide
 * that do not agree about colour, so the product carries an appearance for
 * each. Neither disagreement reaches the artwork — there is one Smartomica
 * lockup, the guide's, and both appearances wear it.
 */
export const BRAND_BY_THEME: Record<ThemeId, BrandId> = {
  terys: 'terysling',
  emerald: 'terysling',
  amber: 'terysling',
  graphite: 'terysling',
  smartomica: 'smartomica',
  smartomicabrand: 'smartomica',
}

/**
 * What the BROWSER shows of a brand: the tab's word and the tab's picture.
 *
 * The name is not catalogue copy and is not translated — a brand does not
 * decline, which is why `app.name` reads `Terysling` in all three catalogues
 * and the vendor's reads `Smartomica` in all three. Stating it here rather
 * than reaching into a catalogue also keeps the tab correct before any locale
 * is resolved.
 *
 * The icons are rasters, because a favicon cannot be a React render, and the
 * tab is the one surface that gets a mark alone with no words beside it.
 * `index.html` ships the host's pair already in place, so what this exists for
 * is the REPLACEMENT — a stored appearance arriving from the account, or a
 * choice made in this session.
 *
 * THE VENDOR'S TAB CARRIES THE GUIDE'S OWN MARK, pixel for pixel: compared
 * against the lockup export inside `smartomica_brand_guide.docx`, the 280×280
 * `smartomicaMark.png` beside this file differs from the sign in that export
 * by nothing at all — not one channel of one pixel. §1.5 names the mark alone
 * as the favicon and app-icon version, so the tab is already wearing exactly
 * what the guide asks it to.
 *
 * A RASTER HERE AND A MASK INSIDE THE INTERFACE is not two decisions about
 * colour — both draw the guide's cyan-to-blue sphere. It is a decision about
 * where the drawing is composited: the interface can paint a vector mask with
 * the guide's gradient at whatever size a surface asks for, while a tab is
 * drawn on browser chrome this product never sees and can only be handed a
 * finished picture.
 */
export const BRAND_FACE: Record<BrandId, { title: string; faviconUrl: string }> = {
  terysling: { title: 'Terysling', faviconUrl: terysFaviconUrl },
  smartomica: { title: 'Smartomica', faviconUrl: smartomicaFaviconUrl },
}
